import { Component, Injector, Input, OnChanges, SimpleChanges } from '@angular/core';
import { RSF, RSFAmendment } from '../../rsf';
import { RSFStatus, RSFPrivileges, RsfService } from '../../rsf.service';
import { BaseComponent } from 'src/app/common/base/base.component';
import { WFAction, WfSignature, WfSignatureResource, WFTaskSignatureResource } from 'src/app/components/workflow/workflow';
import { Tag } from 'src/app/common/enumerations/enumerations';

@Component({
  selector: 'rsf-main-s-base',
  templateUrl: './rsf-main-s-base.component.html',
  styleUrls: ['./rsf-main-s-base.component.scss']
})
export class RsfMainSBaseComponent extends BaseComponent implements OnChanges {
  @Input() rsf?: RSF | null;
  @Input() rsfAmendment?: RSFAmendment | null;
  @Input() disabled?: boolean;
  @Input() status?: RSFStatus;
  @Input() privileges?: RSFPrivileges;
  @Input() service?: RsfService;
  @Input() isLogView?: boolean = false;

  wfAction = WFAction;

  constructor(
    protected override injector: Injector,
  ) {
    super(injector);
  }

  ngOnChanges(changes: SimpleChanges): void {

  }

  /**
   * Persists the current RSF and preserves its dirty state when the API rejects the update.
   * @returns The saved RSF response, or no value when the update fails.
   */
  async update() {
    if (this.rsf) {
      const rsf = this.utils.Serialize(this.rsf);
      return await this.service?.update(rsf).toPromise().catch(error => {
        console.log(error);
        this.alert.defaultError();
      });
    }
    else return null;
  }

  /**
   * Persists the active amendment through the appropriate endpoint without discarding failed edits.
   * @returns The saved amendment response, or no value when the update fails.
   */
  async updateAmendment() {
    if (this.rsfAmendment) {
      this.removeDeletedRestrictions();
      const rsfAmendment = this.utils.Serialize(this.rsfAmendment);
      if (this.status?.isMinorAmendment) {
        this.restoreMinorAmendmentRestrictedValues(rsfAmendment);
      }
      // Use updateMinorAmendment endpoint for minor amendments
      const updateMethod = this.status?.isMinorAmendment
        ? this.service?.updateMinorAmendment(rsfAmendment)
        : this.service?.updateAmendment(rsfAmendment);
      return await updateMethod?.toPromise().catch(error => {
        console.log(error);
        this.alert.defaultError();
      });
    }
    else return null;
  }

  /**
   * Restores restricted scalar values from the original RSF before a Minor Amendment is sent.
   * @param amendment - Serialized amendment payload to normalize.
   */
  private restoreMinorAmendmentRestrictedValues(amendment: RSFAmendment): void {
    if (!this.rsf) {
      return;
    }

    // Read-only controls can emit initialization values; the original RSF remains authoritative.
    const restrictedFields: (keyof RSF)[] = [
      'requesterID',
      'shielding',
      'shieldingRemoveReplace',
      'shieldingModification',
      'shieldingModificationARC',
      'shieldingModificationBRC',
      'pps',
      'ppsMaintenance',
      'ppsModification',
      'ppsModificationARC',
      'ppsModificationBRC',
      'ppsAcis',
      'tombis',
      'ppsOther',
      'ppsOtherText',
      'ppsplcSet',
      'ppsSoftware',
      'etiAccess',
      'tombisplcSet',
      'tombisSoftware',
      'topOffInterlockSetPoint',
      'radMonitors',
      'radMonitorsMoving',
      'radMonitorsDisconnecting',
      'radMonitorsModifying',
      'radMonitorsTripPoint',
      'radSurveyRequired',
      'beamLine',
      'accelerator',
      'otherSystems'
    ];

    restrictedFields.forEach(field => {
      (amendment as any)[field] = (this.rsf as any)[field];
    });
  }

  /**
   * Removes deleted restrictions from the current `rsfAmendment` and reinitializes the IDs of the remaining items.
   *
   * If `this.rsfAmendment` is present, this method:
   * - Filters `rsfAmendment.rsfRestrictions` to remove any entries where `deleted` is truthy.
   * - Sets the `id` property to `0` for each remaining (undeleted) restriction so they are treated as new.
   *
   * This method mutates `this.rsfAmendment` in place. If `this.rsfAmendment` is falsy, the call is a no-op.
   *
   * @returns void
   */
  removeDeletedRestrictions() {
    if (this.rsfAmendment) {
      this.rsfAmendment.rsfRestrictions = this.rsfAmendment?.rsfRestrictions.filter(r => !r.deleted);

      // Undeleted Items must be re-added with ID = 0;
      this.rsfAmendment.rsfRestrictions.filter(r => r.deleted == false).forEach(r => r.id = 0);
    }
  }

  getSignature(tag: Tag, select: SignatureSelector = pickLast): WfSignature | undefined {
    const all: WfSignatureResource[] | undefined =
      this.rsf?.wfTable?.wfTableLocal?.wfSectionLocals
        ?.flatMap(s => s?.wfSignatures ?? []);

    if (!all?.length) {
      console.warn(`Signature not found, ${tag}`);
      return;
    }

    const wfAmendmentID = this.rsf?.rsfAmendments?.find(a => a.active)?.wfAmendmentID ?? 0;

    const candidates = all.filter(s => s?.code == tag && (s.wfAmendmentID ?? 0) == wfAmendmentID);
    if (!candidates.length) {
      console.warn(`Signature not found, ${tag}`);
      return;
    }

    const chosen = select(candidates);
    if (!chosen) {
      console.warn(`Signature not found (selector returned none), ${tag}`);
      return;
    }

    return WfSignatureResource.transformSignature(chosen);
  }

  refresh() { }

  async signTask(wfSignature: WfSignature, actionID: number, data?: any) {
    await this.service?.confirmSign(this.rsf, wfSignature, actionID, data)
      .catch(error => {
        this.alert.defaultError();
        console.error(error);
      });
  }

  async sign(s: WfSignature, action: WFAction, data: any = null) {
    let options = undefined;
    if (data?.options) {
      options = this.utils.JSONstringify(data.options);
    }
    const actionID = action == WFAction.Approve ? s.approveActionId : action == WFAction.Unapprove ? s.unapproveActionId : action == WFAction.Disapprove ? s.disapproveActionId : 0;
    const signed = await this.service?.confirmSign(this.rsf, s, actionID, '', options);
    if (!signed) this.refresh();
  }

  isAmendment() {
    return !!this.rsf?.rsfAmendments?.find(a => a.active) && this.status?.isViewingAmendment;
  }
}

export type SectionNumber = number & { readonly __brand: 'SectionNumber'; };
export function Section(n: number): SectionNumber {
  return n as SectionNumber;
}

// Reusable selector type operating on *resources*
type SignatureSelector = (candidates: WfSignatureResource[]) => WfSignatureResource | undefined;

// Common selectors
export const pickLast: SignatureSelector = arr => arr.at(-1);
export const pickFirstUnsigned: SignatureSelector = arr => arr.find(s => !s.signed);

// Optional: compose selectors (try A, else B)
export const pickFirstUnsignedElseLast: SignatureSelector = arr =>
  pickFirstUnsigned(arr) ?? pickLast(arr);
